import Cropper from 'cropperjs';
import { ChangeEvent, LegacyRef, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  fetchAuthenticatedUserDetails,
  useLoggedInEmployeeId,
} from '~/components/Auth';
import { removeProfileImage, updateProfileImage } from '~/data/employeeProfile';
import { EmployeeProfileDto } from '~/services/Http/client.people.generated';
import { AppDispatch } from '~/store';

type CropperActions = {
  zoomImage: (zoomFactor: number) => void;
  rotateImage: (rotateAngle: number) => void;
};

type PictureActions = {
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onRemove: () => void;
  onSelectFile: () => void;
};

export enum ProfilePictureDialogStatus {
  Error = 'error',
  Loading = 'loading',
  Success = 'success',
  Info = 'info',
}

export type ProfilePictureDialogStatusMessage = {
  status?: ProfilePictureDialogStatus;
  type?: 'error' | 'info' | 'success' | 'loading';
  message: string;
};

type UseProfilePictureDialogOutput = {
  cropper: {
    imgUrl: string;
    onInit: () => void;
    ref: (node: HTMLImageElement | null) => void;
    actions: CropperActions;
    previewClassName: string;
  };
  picture: {
    inputFileRef: LegacyRef<HTMLInputElement>;
    actions: PictureActions;
  };
  statusMessage?: ProfilePictureDialogStatusMessage;
};

type PropsType = {
  employeeProfile?: EmployeeProfileDto;
  onSubmitSuccess?: () => void;
};

export const useEditProfilePicture = (
  props: PropsType,
): UseProfilePictureDialogOutput => {
  const { employeeProfile, onSubmitSuccess } = props;
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const inputFileRef: LegacyRef<HTMLInputElement> = useRef(null);
  const [imgNode, setImgNode] = useState<HTMLImageElement>();
  const [cropper, setCropper] = useState<Cropper>();
  const [imgUrl, setImgUrl] = useState(employeeProfile?.profilePic || '');
  const [statusMessage, setStatusMessage] = useState<
    ProfilePictureDialogStatusMessage | undefined
  >(undefined);

  const previewClassName = 'img-preview';

  const onInitCropper = useCallback(() => {
    if (!imgNode || !imgUrl) {
      return;
    }

    if (cropper) {
      cropper.destroy();
    }

    const cropperOptions: Cropper.Options = {
      aspectRatio: 1,
      autoCropArea: 0.9,
      preview: `.${previewClassName}`,
      viewMode: 2,
      background: false,
    };

    const newCropper = new Cropper(imgNode, cropperOptions);
    setCropper(newCropper);
  }, [cropper, imgNode, imgUrl]);

  const cropperActions: CropperActions = {
    zoomImage: (zoomFactor: number) => {
      if (!!cropper) {
        cropper.zoom(zoomFactor);
      }
    },
    rotateImage: (rotateAngle: number) => {
      if (!!cropper) {
        cropper.rotate(rotateAngle);
      }
    },
  };

  const cropperRef = useCallback(
    (node) => {
      if (node !== null) {
        setImgNode(node);
      }
    },
    [setImgNode],
  );

  const finalizeProfilePictureUpdated = useCallback(() => {
    setStatusMessage({
      status: ProfilePictureDialogStatus.Success,
      message: t('profilePictureUpdated'),
    });

    if (!!onSubmitSuccess) {
      onSubmitSuccess();
    }

    // TODO: Maybe reset/clear success message after a while
    // statusMessageTimer.current = setTimeout(() => {
    //   setStatusMessage(undefined);
    // }, 5000);
  }, [t, onSubmitSuccess]);

  const profilePictureUpdated = useCallback(() => {
    if (employeeProfile?.id === loggedInEmployeeId) {
      dispatch(fetchAuthenticatedUserDetails()).then(() => {
        finalizeProfilePictureUpdated();
      });
    } else {
      finalizeProfilePictureUpdated();
    }
  }, [
    dispatch,
    employeeProfile,
    loggedInEmployeeId,
    finalizeProfilePictureUpdated,
  ]);

  const onImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !cropper || !imgNode) {
      return;
    }

    const reader = new FileReader();
    const sizeM = event.target.files[0].size / 1024 / 1024;
    if (sizeM > 10) {
      setStatusMessage({
        status: ProfilePictureDialogStatus.Error,
        type: 'error',
        message: t('laErrorTooBigAfterPicCrop'),
      });

      return;
    }

    setStatusMessage({
      status: ProfilePictureDialogStatus.Loading,
      message: t('laLoading'),
    });
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = () => {
      setStatusMessage(undefined);
      setImgUrl(`${reader.result}`);
    };
  };

  const onImageRemove = useCallback(() => {
    const strElements = employeeProfile?.profilePic.split('/').reverse() || [];
    if (
      !strElements.length ||
      !window.confirm(`${t('laConfirmDelete')} ${t('laItem')}?`) ||
      !employeeProfile?.id
    ) {
      return;
    }

    setStatusMessage({
      status: ProfilePictureDialogStatus.Loading,
      message: t('laLoading'),
    });
    dispatch(
      removeProfileImage({
        employeeId: employeeProfile?.id,
        profilePictureId: strElements[0],
      }),
    ).then(() => {
      profilePictureUpdated();
    });
  }, [t, dispatch, employeeProfile, profilePictureUpdated]);

  const onImageSave = useCallback(() => {
    if (!cropper) {
      return;
    }

    setStatusMessage({
      status: ProfilePictureDialogStatus.Loading,
      message: t('laUploading'),
    });
    cropper.getCroppedCanvas().toBlob((blob) => {
      if (!blob || !employeeProfile?.id) {
        return;
      }

      dispatch(
        updateProfileImage({ employeeId: employeeProfile?.id, blob }),
      ).then(() => {
        profilePictureUpdated();
      });
    });
  }, [t, cropper, dispatch, employeeProfile, profilePictureUpdated]);

  const onImageInputOpen = () => {
    inputFileRef.current?.click();
  };

  return {
    cropper: {
      imgUrl,
      onInit: onInitCropper,
      ref: cropperRef,
      actions: cropperActions,
      previewClassName,
    },
    picture: {
      inputFileRef,
      actions: {
        onUpload: onImageUpload,
        onSave: onImageSave,
        onRemove: onImageRemove,
        onSelectFile: onImageInputOpen,
      },
    },
    statusMessage,
  };
};
